import React, { useEffect, useState } from "react";

import {
	Button,
	NotificationCard,
	OneColumnStack,
	Section,
} from "@arp/react-ndbx";
import { Box, Fade } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import useTheme from "@mui/material/styles/useTheme";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";

import { RetrieveOnlineButtonStack } from "../../../components/organisms/RetrieveOnlineButtonStack";
import { Application } from "../../../config/const/enums";
import { pageOrder, pageOrderCC } from "../../../config/pageInfo/pageOrder";
import { queryKeys } from "../../../hooks/dataHooks/queryKeys";
import { useGetRetrieveQuoteDetails } from "../../../hooks/dataHooks/useGetRetrieveQuoteDetails";
import { getAllQuestions } from "../../../hooks/useAuraService";
import useNewBusiness from "../../../hooks/useNewBusiness";
import usePreQualification from "../../../hooks/usePreQualification";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages";
import useSharedData from "../../../hooks/useSharedData";

import { createQuoteResponseMapping } from "../../../mapping/createQuoteResponseMapping";
import { interviewAuraOutcomeMapping } from "../../../mapping/interviewAuraOutcomeMappingResponse";
import {
	checkAuraUWSubmitted,
	checkAuraUWcompleted,
} from "../../../utilities/auraHealthUtils";
import { getQuoteStatusMapping } from "../../../utilities/preQualificationUtils";
import { backToQuotePoint } from "../../../utilities/utils";

//----------------------------------------------------------------

function RetrieveLoading() {
	const queryClient = useQueryClient();

	const { updateStateOnFreshRetrieval } = usePreQualification();
	const { state, funcs } = usePreQualificationPages();
	const { handleEventChange, isDevShared } = useSharedData();
	const [retrieveQuoteError, setRetrieveQuoteError] = useState(false);
	const theme = useTheme();

	const retrieveQuoteTokenDetail = queryClient.getQueryData([
		queryKeys.RETRIEVE_QUOTE_TOKEN_DETAIL,
	]);

	const { isLoading, data } = useGetRetrieveQuoteDetails(
		retrieveQuoteTokenDetail,
		isDevShared
	);

	const circularProgressStyle = {
		color: `${theme.palette.action.PrimaryAction450}`,
	};

	const { setInitialData } = useNewBusiness();

	const isOnline = state.application === Application.ONLINE ? true : false;

	const getQuoteStateAndNavigate = async (quoteDetails) => {
		const quoteState = getQuoteStatusMapping(quoteDetails.state);
		switch (quoteState) {
			case "QTC":
				if (retrieveQuoteTokenDetail?.expired) {
					// if the quote has expired --> true -->take to expired page
					await funcs.validateAndProceed(
						isOnline ? pageOrder.RETRIEVE_DECLINE : pageOrderCC.RETRIEVE_DECLINE
					);
				} else {
					// else take to the summary page
					await funcs.validateAndProceed(
						isOnline ? pageOrder.COVER : pageOrderCC.COVER
					);
				}
				break;
			case "NBP":
				try {
					if (retrieveQuoteTokenDetail?.expired) {
						// if the quote has expired --> true -->take to expired page
						await funcs.validateAndProceed(
							isOnline
								? pageOrder.RETRIEVE_DECLINE
								: pageOrderCC.RETRIEVE_DECLINE
						);
					} else {
						// else determine whether to take to DOD or Review page
						const getAuraQuestionsResponse = await getAllQuestions(
							quoteDetails.jwtToken
						);
						const auraQuestion = _.first(
							getAuraQuestionsResponse?.responseJson
						);
						const auraUWCompleted = checkAuraUWcompleted(auraQuestion);
						const auraUWSubmitted = checkAuraUWSubmitted(auraQuestion);
						if (!auraUWCompleted) {
							// if auraUWCompleted is false,means user has started the UW and didn't completed it, so user will go to DOD page
							/*5. Updating the pages state, pass the index of the previous page to that the method can update the next 
              in line page and redirect the user to that page*/
							// await funcs.validateAndProceed(4);
							await funcs.validateAndProceed(
								isOnline ? pageOrder.QUOTE : pageOrderCC.QUOTE
							);
						} else if (auraUWCompleted && !auraUWSubmitted) {
							// UW completed NOT submitted--> review page
							/*5. Updating the pages state, pass the index of the previous page to that the method can update the next 
              in line page and redirect the user to that page*/
							// await funcs.validateAndProceed(6);
							await funcs.validateAndProceed(
								isOnline
									? pageOrder.ABOUT_YOU_QUESTIONS
									: pageOrderCC.ABOUT_YOU_QUESTIONS
							);
						}
					}
				} catch (error) {
					setRetrieveQuoteError(true);
				}
				break;
			case "NBC":
				await funcs.validateAndProceed(
					isOnline ? pageOrder.PP_ONLINE_PAYMENT : pageOrderCC.PP_CC_PAYMENT,
					true
				);
				break;
			case "NBY":
				//* The Outcome page needs to know if the Policy is expired.
				const isExpired = retrieveQuoteTokenDetail?.expired;
				if (isExpired) {
					handleEventChange({
						name: "isRetrievePolicyExpired",
						value: true,
					});
				}

				await funcs.validateAndProceed(
					isOnline
						? pageOrder.PP_ONLINE_POLICY_OUTCOME
						: pageOrderCC.PP_CC_POLICY_OUTCOME,
					true
				);
				break;
			case "NBA":
			case "NBU":
				await funcs.validateAndProceed(
					isOnline
						? pageOrder.RETRIEVE_REFERRED_UW
						: pageOrderCC.RETRIEVE_REFERRED_UW
				);
				break;
			case "NBD":
				await funcs.validateAndProceed(
					isOnline
						? pageOrder.RETRIEVE_QUOTE_LOADING
						: pageOrderCC.RETRIEVE_QUOTE_LOADING
				);
				break;
			case "NBR":
				await funcs.validateAndProceed(
					isOnline ? pageOrder.RETRIEVE_EXPIRED : pageOrderCC.RETRIEVE_EXPIRED
				);
				break;
			default:
				setRetrieveQuoteError(true);
				break;
		}
	};

	useEffect(() => {
		async function fetchData() {
			try {
				if (data) {
					if (data?.expired) {
						// incase of online the response will give expired flag and we need to take the user to the expired page
						queryClient.setQueryData([queryKeys.QUOTE_DATA_GET], data || {});
						await funcs.validateAndProceed(
							isOnline
								? pageOrder.RETRIEVE_DECLINE
								: pageOrderCC.RETRIEVE_DECLINE
						);
					} else if (data?.response) {
						/* this is the scenario for 403 forbidden error in case Online customer is trying to retrieve NBA and NBU policy.
            Send the customer to the Policy bound page */
						if (data?.response?.status === 403 && isOnline) {
							await funcs.validateAndProceed(pageOrder.RETRIEVE_REFERRED_UW);
						} else {
							setRetrieveQuoteError(true);
						}
					} else {
						// If retrieve API gives a response we need to proceed further with mapping
						const policyInfoMapping = interviewAuraOutcomeMapping(data);
						let updatedStateObject = { ...data, ...policyInfoMapping };
						// 1. Updating the state with person info and policy info
						await updateStateOnFreshRetrieval({ data: updatedStateObject });
						// 2. Updating the outcome state as well
						setInitialData(policyInfoMapping);

						const contractStageAndStatus =
							policyInfoMapping?.policyState?.contractStage +
							policyInfoMapping?.policyState?.processStatus;

						if (
							contractStageAndStatus === "NBY" ||
							contractStageAndStatus === "NBC" ||
							contractStageAndStatus === "NBA"
						) {
							queryClient.setQueryData(
								[queryKeys.ORIGINAL_AURA_OUTCOME],
								policyInfoMapping
							);
						}
						// 3. Adding the response to the tanstack cache
						const cacheMappingResponse = createQuoteResponseMapping(
							{ data: data },
							"retrieve"
						);
						queryClient.setQueryData(
							[queryKeys.QUOTE_DATA_GET],
							cacheMappingResponse || {}
						);
						// 4. Updating the shared Data with the JWT Token
						handleEventChange({
							name: "accessToken",
							value: data.jwtToken,
						});
						getQuoteStateAndNavigate(data);
					}
				} else {
					if (!isLoading) {
						setRetrieveQuoteError(true);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		}
		fetchData();
	}, [data]);

	if (retrieveQuoteError) {
		return (
			<Section>
				<Section md={6}>
					<Fade in={true} timeout={500}>
						<Box>
							<NotificationCard
								cardType="error"
								title="Unable to retrieve Quote information"
								message="Some error occurred while retrieving the quote information"
							/>
						</Box>
					</Fade>
				</Section>
				{!isOnline ? (
					<OneColumnStack flexShrink={1} mb={7} mt={9}>
						<Button
							id="cc-processing-continue"
							ariaLabel="continue"
							variant="contained"
							color="primary"
							size="medium"
							type="button"
							onClick={() => backToQuotePoint(Application.CONTACT_CENTER)}
						>
							Back to Search page
						</Button>
					</OneColumnStack>
				) : (
					<Section md={10} mt={9} mb={12}>
						<RetrieveOnlineButtonStack
							backToQuotePoint={backToQuotePoint}
							showCallButton={false}
						/>
					</Section>
				)}
			</Section>
		);
	}
	return (
		<Section
			minHeight="300px"
			display="flex"
			justifyContent="center"
			alignItems="center"
		>
			<CircularProgress style={circularProgressStyle} />
		</Section>
	);
}

export default RetrieveLoading;
